import { useDeleteOffersCargoSubscription } from "@/entities/delete-offers-cargo-subscription/fetch";
import { useGetContractedCompanies } from "@/entities/get-contracted-companies/fetch";
import { useGetOffersCargoSubscriptions } from "@/entities/get-offers-cargo-subscriptions/fetch";
import { usePostOffersCargoSubscriptions } from "@/entities/post-offers-cargo-subscriptions/fetch";
import { transformLoadables } from "@/shared/lib/loadable";
import { loadableFromUseQueryResult } from "@/shared/lib/tanstack-query";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { OfferCargoSubscriptionManagementProps } from "../model";

export const useOfferCargoSubscriptionManagement = (
  companyId: number,
): OfferCargoSubscriptionManagementProps => {
  const queryClient = useQueryClient();

  // データ取得
  const subscriptionsQuery = useGetOffersCargoSubscriptions({
    subscriberCompanyId: companyId,
  });

  const companiesQuery = useGetContractedCompanies({});

  // 追加
  const postSubscriptionMutation = usePostOffersCargoSubscriptions();

  const addSubscription = useCallback(
    async (targetCompanyId: number) => {
      await postSubscriptionMutation.mutateAsync({
        data: {
          offerCargoCompanyId: targetCompanyId,
          subscriberCompanyId: companyId,
        },
      });
      await subscriptionsQuery.refetch();
    },
    [postSubscriptionMutation, companyId, subscriptionsQuery.refetch],
  );

  // 削除
  const deleteSubscriptionMutation = useDeleteOffersCargoSubscription();

  const deleteSubscription = useCallback(
    async (subscriptionId: number) => {
      await deleteSubscriptionMutation.mutateAsync({
        id: subscriptionId,
      });
      // データを再取得
      queryClient.invalidateQueries({
        queryKey: ["getOffersCargoSubscriptions"],
      });
    },
    [deleteSubscriptionMutation, queryClient],
  );

  // Props
  const subscriptions: OfferCargoSubscriptionManagementProps["subscriptions"] =
    useMemo(() => {
      return transformLoadables(
        [loadableFromUseQueryResult(subscriptionsQuery)],
        (data) => data.data.offerCargoSubscriptions ?? [],
      );
    }, [subscriptionsQuery]);

  const companies: OfferCargoSubscriptionManagementProps["companies"] =
    useMemo(() => {
      return transformLoadables(
        [loadableFromUseQueryResult(companiesQuery)],
        (data) =>
          (data.data.companies ?? []).filter((company) =>
            company.category?.includes("shipper"),
          ),
      );
    }, [companiesQuery]);

  return {
    companyId,
    subscriptions,
    companies,
    addSubscription,
    deleteSubscription,
    isAddingSubscription: postSubscriptionMutation.isPending,
    isDeletingSubscription: deleteSubscriptionMutation.isPending,
  };
};
